import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { usePerspective } from "../lib/perspective";
import Hint from "../components/Hint";
import Markdown from "../lib/markdown";
import { DeepLinkBar } from "../components/DeepLink";
import { dashboardUrl, openProjectProgramUrl, kpiDashboardKey, driverTreeRoute, evidenceRoute } from "../lib/deeplinks";
import { Link } from "react-router-dom";
import BeratertagePanel from "../components/BeratertagePanel";  /* cp1025 */

/** ---- Typen (in Sync mit dem kpi-orchestrator, dev-v129) ---- */
/** cp995: Effizienzprogramm (Projekt Blue) */
type BlueMassnahme = { code: string; ressort: string; titel: string; beschreibung?: string;
  gewicht_pct?: number | null; ki_massnahme?: boolean; umsetzungsstatus?: string };
type BlueTp = { code: string; ressort: string; titel: string; ki_teilprojekt?: boolean; ressort_lead?: string };
type BlueRessort = { ressort: string; anzahl: number; gewicht_pct: number; ki_anzahl: number;
  ki_gewicht_pct: number; umsetzungsgrad_pct?: number | null; teilprojekte?: string[]; ki_teilprojekte?: string[] };
type BlueProgramm = {
  programm?: { name?: string; phase?: string; entscheidungspunkt?: string; quelle?: string; hinweis?: string };
  summary?: { massnahmen: number; ki_massnahmen: number; ki_anteil_pct?: number | null;
    umsetzungsgrad_pct?: number | null; je_status?: Record<string, number>;
    teilprojekte?: number; ki_teilprojekte?: number };
  je_ressort?: BlueRessort[]; teilprojekte?: BlueTp[]; massnahmen?: BlueMassnahme[];
};

type TrustCheck = { id: string; status: string; title: string; detail: string; release_blocking?: boolean };
type TrustGate = {
  status: string; checked_at?: string; trusted_source_count?: number; source_count?: number;
  metric_contract_count?: number; quality_threshold?: number; checks?: TrustCheck[];
};
type CockpitSummary = { generated_at?: string; has_data?: boolean; onboarding_progress?: number; trust_gate?: TrustGate };
type MetricLatest = {
  metric_key: string; display_name: string; unit: string; direction: string;
  target_value: number | null; value: number | null; ts: string | null; source: string | null;
  quality: string | null; value_count: number; attainment_pct: number | null; target_reached: boolean | null;
};
type BoardCell = { phase: string; nr?: number; status: string | null; ampel: "gruen" | "gelb" | "rot" | "grau" };
type BoardRow = { ressort: string; label: string; cells: BoardCell[] };
type BoardPhase = { phase: string; nr?: number; titel: string };
type BoardRessort = {
  ressort: string; label: string; lead?: string | null; impulse_team?: string[];
  tps?: string[]; spoc?: string | null; hr_oe_experte?: string | null;
};
type CellReview = {
  status: "bestaetigt" | "korrigiert"; corrected_status?: string | null; comment?: string;
  reviewed_by?: string; reviewed_at?: string; original_status?: string | null;
  evidence_refs?: string[]; applied_at?: string | null; applied_by?: string | null;
};
type Board = {
  available: boolean; reason?: string; updated_at?: string | null; source?: string;
  ressorts: BoardRessort[]; phases: BoardPhase[]; matrix: BoardRow[];
  summary?: { cells_total?: number; in_line?: number; in_verzug?: number; nicht_begonnen?: number; progress_pct?: number };
  reviews?: { cells: Record<string, CellReview>; summary?: { bestaetigt?: number; korrigiert?: number } };
};
type CellSel = { ressort: string; label: string; phase: string; titel: string; nr?: number; status: string | null; ampel: string };
type Progress = { completed_count?: number; total_steps?: number; progress_pct?: number };
type BriefingItem = {
  id: string; title?: string; markdown?: string; model_route?: string; degraded?: boolean;
  created_at?: string; kpi_count?: number; measured_count?: number;
};
type Job = { job_id: string; status: "queued" | "running" | "succeeded" | "failed"; error?: string | null };
type AskResp = { answer: string; degraded?: boolean; model?: string | null; grounded?: boolean; source?: string };

const OPENPROJECT_BASE = "https://project.impulse.kim";
const OPENSEARCH_COCKPIT =
  "https://opensearch.ben-e-fit.ai/app/dashboards#/view/kid021-1und1-cockpit";

function fmtDate(iso?: string | null): string {
  if (!iso) return "unbekannt";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "unbekannt" : d.toLocaleString("de-DE");
}
function fmtVal(v: number | null, unit: string): string {
  if (v == null) return "—";
  if (unit === "%") return v.toFixed(1) + " %";
  if (unit === "Index") return v.toFixed(0);
  return `${v.toLocaleString("de-DE", { maximumFractionDigits: 2 })} ${unit}`.trim();
}
function attClass(pct: number | null, reached: boolean | null): "gut" | "mittel" | "kritisch" | "unbekannt" {
  if (reached) return "gut";
  if (pct == null) return "unbekannt";
  if (pct >= 90) return "gut";
  if (pct >= 70) return "mittel";
  return "kritisch";
}
const AMPEL_TXT: Record<string, string> = {
  gruen: "in Arbeit / im Plan", gelb: "noch nicht begonnen", rot: "in Verzug", grau: "kein Status",
};

/** Technische Metrik-Keys lesbar machen (Anzeige-Politur, Iteration 2).
 *  Der Original-Key bleibt als Tooltip erhalten — nichts wird versteckt. */
const TECH_NAME_MAP: Record<string, string> = {
  openproject_projects_total: "OpenProject: Projekte gesamt",
  openproject_work_packages_total: "OpenProject: Arbeitspakete gesamt",
  openproject_work_packages_open: "OpenProject: Arbeitspakete offen",
  openproject_work_packages_closed: "OpenProject: Arbeitspakete abgeschlossen",
  openproject_due_date_coverage_pct: "OpenProject: Terminierungsquote",
};
function niceName(k: MetricLatest): string {
  if (TECH_NAME_MAP[k.metric_key]) return TECH_NAME_MAP[k.metric_key];
  const n = k.display_name || k.metric_key;
  return n.includes("_") ? n.replace(/_/g, " ") : n;
}

/** Kompakte, EHRLICHE KPI-Ampel-Kachel. Ohne Messwert kein Fantasiewert. */
function KpiAmpel({ k }: { k: MetricLatest }) {
  const measured = k.value != null;
  const cls = attClass(k.attainment_pct, k.target_reached);
  const bar = k.attainment_pct == null ? 0 : Math.max(0, Math.min(100, k.attainment_pct));
  return (
    <article className={`sk-kpi att-${measured ? cls : "leer"}`}>
      <div className="sk-kpi-head">
        <span className="sk-kpi-name" title={k.metric_key}>{niceName(k)}</span>
        {measured ? (
          <span className="sk-dot ok" title={`Stand ${fmtDate(k.ts)} · Quelle ${k.source ?? "?"}`} />
        ) : (
          <span className="sk-dot leer" title="Noch kein Messwert" />
        )}
      </div>
      {measured ? (
        <>
          <div className="sk-kpi-val">{fmtVal(k.value, k.unit)}</div>
          <div className="sk-kpi-target">
            <span>
              Ziel {fmtVal(k.target_value, k.unit)}
              <Hint term="zielerreichung" />
            </span>
            <strong>{k.attainment_pct != null ? `${k.attainment_pct.toFixed(1)} %` : "—"}{k.target_reached ? " ✓" : ""}</strong>
          </div>
          <div className="sk-bar"><span style={{ width: `${bar}%` }} /></div>
          <p className="sk-kpi-note">Stand {fmtDate(k.ts)} · Quelle {k.source ?? "—"}</p>
        </>
      ) : (
        <>
          <div className="sk-kpi-val leer">Noch keine Daten</div>
          <p className="sk-kpi-note leer">
            Quelle ausstehend{k.target_value != null ? ` · Ziel ${fmtVal(k.target_value, k.unit)}` : ""} —
            erscheint automatisch nach Datenlieferung.
          </p>
        </>
      )}
      {/* BEN-92: einheitliche Deep-Links je KPI — Dashboard, Treiberbaum, (OpenProject bei OP-Metriken) */}
      <DeepLinkBar
        className="sk-kpi-links"
        links={[
          {
            key: "dash",
            href: dashboardUrl(kpiDashboardKey(k.metric_key)),
            label: "Im Dashboard ansehen",
            kind: "dashboard",
            title: `${niceName(k)} im OpenSearch-Dashboard ansehen`,
          },
          {
            key: "tree",
            href: driverTreeRoute(),
            label: "Treiberbaum",
            kind: "intern",
            title: "Zum Treiberbaum (Ursache → Wirkung) springen",
          },
          {
            key: "ivk",
            href: "/app/interventionen",
            label: "Passende Intervention",
            kind: "intern",
            title: "Interventionskatalog: passende Maßnahme zu dieser Kennzahl",
          },
          {
            key: "run",
            href: evidenceRoute(),
            label: "Belege & Runs",
            kind: "intern",
            title: "Nachweise und Agent-Runs ansehen",
          },
          k.metric_key.startsWith("openproject_")
            ? {
                key: "op",
                href: openProjectProgramUrl(),
                label: "In OpenProject öffnen",
                kind: "openproject",
                title: "Arbeitspakete des 1&1-Programms in OpenProject öffnen",
              }
            : null,
        ]}
      />
    </article>
  );
}

export default function SteuerkreisPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const { isBerater, perspective } = usePerspective();
  const qc = useQueryClient();

  const summary = useQuery({ queryKey: ["sk-summary", effectiveTenantId], queryFn: () => api.get<CockpitSummary>("/cockpit/summary") });
  const kpis = useQuery({ queryKey: ["sk-kpis", effectiveTenantId], queryFn: () => api.get<MetricLatest[]>("/metrics/values/latest") });
  const board = useQuery({ queryKey: ["sk-board", effectiveTenantId], queryFn: () => api.get<Board>("/transformation/board") });
  const progress = useQuery({ queryKey: ["sk-progress", effectiveTenantId], queryFn: () => api.get<Progress>("/process-landscape/progress") });
  const briefings = useQuery({ queryKey: ["sk-briefings", effectiveTenantId], queryFn: () => api.get<BriefingItem[]>("/agents/briefings?limit=1") });
  const blue = useQuery({ queryKey: ["sk-blue", effectiveTenantId], queryFn: () => api.get<BlueProgramm>("/transformation/effizienzprogramm") });
  const rollen = useQuery({ queryKey: ["sk-rollen", effectiveTenantId], queryFn: () => api.get<any>("/transformation/rollenmatrix") });  /* cp1009-rollen */

  /** Briefing erzeugen (Job pollen) */
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const job = useQuery({
    queryKey: ["sk-job", jobId],
    queryFn: () => api.get<Job>(`/agents/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const s = (q.state.data as Job | undefined)?.status;
      return s === "succeeded" || s === "failed" ? false : 2500;
    },
  });
  useEffect(() => {
    const s = job.data?.status;
    if (s === "succeeded") { qc.invalidateQueries({ queryKey: ["sk-briefings", effectiveTenantId] }); setBusy(false); }
    if (s === "failed") { setBusy(false); setErr(job.data?.error ?? "Briefing fehlgeschlagen."); }
  }, [job.data?.status]);
  const running = busy || job.data?.status === "queued" || job.data?.status === "running";

  /** Menschliche Querprüfung: ausgewählte Matrixzelle */
  const [cellSel, setCellSel] = useState<CellSel | null>(null);

  const generateBriefing = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post<{ job_id: string }>("/agents/briefing", { audience: "ceo", type: "weekly" });
      setJobId(r.job_id);
    } catch (e: any) { setBusy(false); setErr(e?.details?.detail ?? e?.message ?? "Start fehlgeschlagen."); }
  };
  const exportBriefing = async (format: string) => {
    setErr(null);
    try {
      const r = await api.post<{ job_id: string }>("/agents/export", { format, audience: "ceo", type: "executive_summary" });
      setJobId(r.job_id);
    } catch (e: any) { setErr(e?.details?.detail ?? "Export fehlgeschlagen."); }
  };

  /** Frag die Daten */
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<AskResp | null>(null);
  const [askErr, setAskErr] = useState<string | null>(null);
  const ask = async (q?: string) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setAsking(true); setAskErr(null); setAnswer(null);
    try {
      const r = await api.post<AskResp>("/transformation/ask", { question: text });
      setAnswer(r);
    } catch (e: any) { setAskErr(e?.details?.detail ?? e?.message ?? "Frage fehlgeschlagen."); }
    finally { setAsking(false); }
  };
  const SUGGESTIONS = [
    "Welche KPI hat die niedrigste Zielerreichung?",
    "Wo steht das Programm insgesamt?",
    "Welche Ressorts sind in Verzug?",
    "Welche Kennzahlen haben noch keine Messwerte?",
  ];

  const gate = summary.data?.trust_gate;
  const measuredCount = (kpis.data ?? []).filter((k) => k.value != null).length;
  const kpiTotal = kpis.data?.length ?? 0;
  const latest = briefings.data?.[0];
  const b = board.data;

  /** Programm-Ampel (Iteration 2): EINE ehrliche Gesamtaussage zuoberst.
   *  rot   = mind. ein Ressort in Verzug oder eine gemessene KPI kritisch (< 70 %)
   *  gelb  = Programm im Aufbau (Fortschritt < 60 % oder Mehrheit der KPIs ohne Messwert)
   *  gruen = im Plan */
  const kritKpis = (kpis.data ?? []).filter(
    (k) => k.value != null && !k.target_reached && k.attainment_pct != null && k.attainment_pct < 70,
  ).length;
  const ampel = b?.available
    ? (() => {
        const verzug = b.summary?.in_verzug ?? 0;
        const progress = b.summary?.progress_pct ?? 0;
        if (verzug > 0 || kritKpis > 0)
          return {
            cls: "rot", label: "Handlungsbedarf",
            why: [
              verzug > 0 ? `${verzug} Ressort${verzug > 1 ? "s" : ""} in Verzug` : null,
              kritKpis > 0 ? `${kritKpis} KPI kritisch` : null,
            ].filter(Boolean).join(" · "),
          };
        if (progress < 60 || measuredCount * 2 < kpiTotal)
          return { cls: "gelb", label: "Im Aufbau", why: `${progress} % Fortschritt · ${measuredCount}/${kpiTotal} KPIs gemessen` };
        return { cls: "gruen", label: "Im Plan", why: `${progress} % Fortschritt · kein Ressort in Verzug` };
      })()
    : null;

  return (
    <section className="sk-page">
      <header className="sk-hero">
        <div>
          <p className="hero-eyebrow">Steuerkreis-Cockpit · {perspective === "berater" ? "Beratersicht (Impulse)" : "Managementsicht (1&1)"}</p>
          <h1>Transformation 1&amp;1 — Wo stehen wir?</h1>
          <p className="hero-sub">
            Alles für den Steuerkreis auf einer Seite: Programmfortschritt, KPI-Ampeln, Phasen-Gate-Matrix,
            das letzte KI-Steuerungsbriefing und die Datenlage. Jede Zahl hat einen Beleg — fehlt ein Messwert,
            steht das ehrlich dort.
          </p>
        </div>
        <div className="sk-status-col">
          {ampel && (
            <div className={`sk-ampel ${ampel.cls}`} role="status" aria-label={`Programm-Ampel: ${ampel.label}`}>
              <span className="sk-ampel-dot" aria-hidden />
              <div className="sk-ampel-copy">
                <span className="sk-ampel-k">Programm-Ampel</span>
                <strong>{ampel.label}</strong>
                <span>{ampel.why}</span>
              </div>
            </div>
          )}
          <div className={`sk-trust sk-trust-${gate?.status ?? "unknown"}`}>
            <span className="sk-trust-label"><Hint term="trust_gate">Datenlage</Hint></span>
            <strong>{gate?.status === "trusted" ? "vertrauenswürdig" : gate?.status ?? "—"}</strong>
            <span className="sk-trust-meta">
              {gate?.trusted_source_count ?? 0}/{gate?.source_count ?? 0} Quellen · Stand {fmtDate(summary.data?.generated_at)}
            </span>
          </div>
        </div>
      </header>

      <div className="sk-tiles">
        <div className="sk-tile">
          <span className="sk-tile-k">Programmfortschritt</span>
          <strong>{b?.available ? `${b.summary?.progress_pct ?? 0} %` : "—"}</strong>
          <small>{b?.available ? `${b.summary?.in_line ?? 0} von ${b.summary?.cells_total ?? 0} Gate-Feldern „im Plan“` : "Gate-Matrix nicht lesbar"}</small>
        </div>
        <div className="sk-tile">
          <span className="sk-tile-k">KPIs mit Messwert</span>
          <strong>{measuredCount}/{kpiTotal}</strong>
          <small>{kpiTotal - measuredCount} Kennzahlen warten noch auf Daten</small>
        </div>
        <div className="sk-tile">
          <span className="sk-tile-k">Ressorts in Verzug</span>
          <strong className={b?.summary?.in_verzug ? "bad" : "ok"}>{b?.available ? (b.summary?.in_verzug ?? 0) : "—"}</strong>
          <small>rote Gate-Felder im OE-Board</small>
        </div>
        <div className="sk-tile">
          <span className="sk-tile-k"><Hint term="teilprojekt">Teilprojekt-Schritte</Hint></span>
          <strong>{progress.data?.completed_count ?? 0}/{progress.data?.total_steps ?? 0}</strong>
          <small>{progress.data?.progress_pct ?? 0} % der Prozessschritte erledigt</small>
        </div>
      </div>

      <section className="sk-block">
        <div className="sk-block-head">
          <h2>KPI-Ampeln — Ist / Ziel / Zielerreichung</h2>
          <span className="muted small">grün ≥ 90 % · gelb ≥ 70 % · rot darunter · grau: noch kein Messwert</span>
        </div>
        {kpis.isLoading ? <p className="muted">Kennzahlen werden geladen …</p> : (
          <div className="sk-kpi-grid">
            {(kpis.data ?? []).map((k) => <KpiAmpel key={k.metric_key} k={k} />)}
          </div>
        )}
        {kpiTotal > 0 && measuredCount < kpiTotal && (
          <p className="sk-honest">
            Ehrlich gesagt: {measuredCount} von {kpiTotal} Kennzahlen sind gemessen. Die übrigen bleiben grau,
            bis ihre Quelle liefert — wir zeigen bewusst keinen erfundenen Wert.
          </p>
        )}
      </section>

      <section className="sk-block">
        <div className="sk-block-head">
          <h2>Phasen-Gate-Matrix — Ressort × Phase<Hint term="gate_matrix" /></h2>
          <span className="muted small">{b?.available ? `Quelle: ${b.source} · Stand ${fmtDate(b.updated_at)}` : ""}</span>
          {b?.available && (
            <span className="sk-chip" title="Menschliche Querprüfung: Zelle anklicken, Herkunft prüfen, bestätigen oder korrigieren">
              {`menschlich geprüft: ${(b.reviews?.summary?.bestaetigt ?? 0) + (b.reviews?.summary?.korrigiert ?? 0)} / ${b.summary?.cells_total ?? 0}`}
            </span>
          )}
        </div>
        {board.isLoading ? <p className="muted">Board wird geladen …</p> : !b?.available ? (
          <div className="empty-friendly"><strong>Gate-Matrix derzeit nicht lesbar.</strong>
            <p className="muted">{b?.reason ?? "Das OE-Board ist gerade nicht erreichbar. Es werden bewusst keine Ersatzwerte gezeigt."}</p></div>
        ) : (
          <div className="sk-matrix-wrap">
            <table className="sk-matrix">
              <thead>
                <tr>
                  <th className="sk-matrix-corner">Ressort</th>
                  {b.phases.map((p) => <th key={p.phase} title={p.titel}>{p.nr}. {p.titel}</th>)}
                </tr>
              </thead>
              <tbody>
                {b.matrix.map((row) => (
                  <tr key={row.ressort}>
                    <th className="sk-matrix-row">{row.label}</th>
                    {row.cells.map((c) => {
                      const rv = b.reviews?.cells?.[`${row.ressort}|${c.phase}`];
                      const eff = rv?.status === "korrigiert" && rv.corrected_status
                        ? (RV_AMPEL[rv.corrected_status] ?? c.ampel) : c.ampel;
                      const extra = rv
                        ? (rv.status === "bestaetigt" ? ` · menschlich bestätigt (${rv.reviewed_by ?? ""})` : ` · menschlich korrigiert (${rv.reviewed_by ?? ""})`)
                        : " · unbestätigt — klicken für Herkunft & Prüfung";
                      return (
                        <td key={c.phase} className={`sk-cell amp-${eff}`}
                            style={{ position: "relative", cursor: "pointer" }}
                            title={`${row.label} · Phase ${c.nr}: ${AMPEL_TXT[eff]}${extra}`}
                            onClick={() => setCellSel({
                              ressort: row.ressort, label: row.label, phase: c.phase,
                              titel: b.phases.find((p) => p.phase === c.phase)?.titel ?? c.phase,
                              nr: c.nr, status: c.status, ampel: eff,
                            })}>
                          <span className="sk-cell-dot" />
                          {rv && (
                            <span style={{ position: "absolute", top: 1, right: 4, fontSize: 10, lineHeight: "12px", opacity: 0.95 }}>
                              {rv.status === "bestaetigt" ? "✓" : "✎"}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sk-legend">
              <span><i className="amp-gruen" /> im Plan / in Arbeit</span>
              <span><i className="amp-gelb" /> noch nicht begonnen</span>
              <span><i className="amp-rot" /> in Verzug</span>
              <span><i className="amp-grau" /> kein Status</span>
              <span title="Jede Zelle ist anklickbar: Herkunft der Aussage, Quellen, Fragen an eine lesende KI, menschliche Bestätigung oder Korrektur.">✓ menschlich bestätigt · ✎ menschlich korrigiert · Zelle anklicken für Herkunft &amp; Prüfung</span>
            </div>
          </div>
        )}
      </section>


      {/* cp1009-rollen: Rolle, Verantwortung, Unterstuetzung */}
      {rollen.data?.rollen?.length ? (
        <section className="sk-card" style={{ marginTop: 18 }}>
          <h2>Rollen, Verantwortung und Unterstützung</h2>
          <p className="small muted" style={{ marginTop: 2 }}>
            Quelle: {rollen.data.quelle}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, margin: "14px 0" }}>
            {[
              { t: "Rollen im Programm", v: `${rollen.data.kennzahlen?.rollen ?? 0}`,
                s: `${rollen.data.kennzahlen?.rollen_besetzt ?? 0} namentlich besetzt` },
              { t: "Teilprojekte", v: `${rollen.data.kennzahlen?.teilprojekte ?? 0}`,
                s: `davon ${rollen.data.kennzahlen?.ki_teilprojekte ?? 0} mit KI-Fokus` },
              { t: "Interventionen im Katalog", v: `${rollen.data.kennzahlen?.interventionen_im_katalog ?? 0}`,
                s: "als Unterstützung zugeordnet" },
              { t: "Rollen ohne Angebot", v: `${rollen.data.kennzahlen?.rollen_ohne_unterstuetzung ?? 0}`,
                s: "keine passende Intervention gefunden" },
            ].map((k) => (
              <div key={k.t} style={{ flex: "1 1 200px", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, opacity: .75 }}>{k.t}</div>
                <div style={{ fontSize: 26, fontWeight: 700, margin: "4px 0" }}>{k.v}</div>
                <div style={{ fontSize: 12, opacity: .6 }}>{k.s}</div>
              </div>
            ))}
          </div>
          {rollen.data.hinweis ? (
            <p className="small muted" style={{ margin: "0 0 10px" }}>{rollen.data.hinweis}</p>
          ) : null}
          <div style={{ overflowX: "auto" }}>
            <table className="sk-table" style={{ width: "100%", fontSize: 13 }}>
              <thead><tr>
                <th style={{ textAlign: "left", width: "18%" }}>Rolle</th>
                <th style={{ textAlign: "left", width: "26%" }}>Wofür verantwortlich</th>
                <th style={{ textAlign: "left", width: "22%" }}>Wie sie wirkt</th>
                <th style={{ textAlign: "left", width: "34%" }}>Womit wir unterstützen können</th>
              </tr></thead>
              <tbody>
                {rollen.data.rollen.map((r: any) => (
                  <tr key={r.rolle}>
                    <td style={{ textAlign: "left", verticalAlign: "top" }}>
                      <strong>{r.rolle}</strong>
                      <div style={{ fontSize: 11.5, opacity: .7, marginTop: 3 }}>
                        {r.besetzt ? r.traeger.join(" · ") : "nicht namentlich besetzt"}
                      </div>
                      <div style={{ fontSize: 11.5, opacity: .55, marginTop: 3 }}>
                        berührt {r.reichweite_teilprojekte} Teilprojekte
                      </div>
                    </td>
                    <td style={{ textAlign: "left", verticalAlign: "top" }}>{r.verantwortung}</td>
                    <td style={{ textAlign: "left", verticalAlign: "top" }}>
                      {r.wirkweise}
                      {r.ki_hinweis ? (
                        <div style={{ marginTop: 6, fontSize: 12, color: "var(--warn-strong)" }}>
                          KI: {r.ki_hinweis}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ textAlign: "left", verticalAlign: "top" }}>
                      {r.unterstuetzung?.length ? (
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {r.unterstuetzung.map((iv: any) => (
                            <li key={iv.code} style={{ marginBottom: 5 }}>
                              <code>{iv.code}</code> {iv.name}
                              {iv.wann_sinnvoll ? (
                                <div style={{ fontSize: 11.5, opacity: .62 }}>
                                  sinnvoll, wenn: {iv.wann_sinnvoll}
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span style={{ opacity: .6 }}>keine passende Intervention hinterlegt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            Rollen und Verantwortlichkeiten stammen aus der hinterlegten Kernteam-Unterlage.
            Die Zuordnung der Interventionen ist ein Vorschlag aus dem Katalog — sie ersetzt
            kein Gespräch, sie bereitet es vor.
          </p>
        </section>
      ) : null}

      {/* cp998-panel: Effizienzprogramm — Anteile und Umsetzungsgrad, keine Betraege */}
      {blue.data?.summary ? (
        <section className="sk-card" style={{ marginTop: 18 }}>
          <h2>Effizienzprogramm x OE-Transformation</h2>
          <p className="sk-sub">
            {blue.data.programm?.phase ? blue.data.programm.phase + " · " : ""}
            {blue.data.programm?.entscheidungspunkt ? "Entscheidungspunkt: " + blue.data.programm.entscheidungspunkt + " · " : ""}
            Betraege und Programmbezeichnung sind vertraulich und werden hier bewusst nicht gezeigt —
            dargestellt sind relative Gewichte und der Umsetzungsgrad.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, margin: "14px 0" }}>
            {[
              { t: "Massnahmen erfasst", v: `${blue.data.summary.massnahmen}`,
                s: `verteilt auf ${blue.data.summary.teilprojekte ?? 0} Teilprojekte` },
              { t: "Umsetzungsgrad", v: `${blue.data.summary.umsetzungsgrad_pct ?? 0} %`,
                s: "gewichtet nach Anteil am Gesamtpotenzial" },
              { t: "KI-Massnahmen", v: `${blue.data.summary.ki_anteil_pct ?? 0} %`,
                s: `${blue.data.summary.ki_massnahmen} Massnahmen · ${blue.data.summary.ki_teilprojekte ?? 0} KI-Teilprojekte` },
              { t: "Noch nicht bewertet", v: `${blue.data.summary.je_status?.nicht_bewertet ?? 0}`,
                s: "Status durch die Verantwortlichen zu setzen" },
            ].map((k) => (
              <div key={k.t} style={{ flex: "1 1 200px", border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, opacity: .75 }}>{k.t}</div>
                <div style={{ fontSize: 26, fontWeight: 700, margin: "4px 0" }}>{k.v}</div>
                <div style={{ fontSize: 12, opacity: .6 }}>{k.s}</div>
              </div>
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="sk-table sk-blue-tab" style={{ width: "100%", fontSize: 13 }}>
              <thead><tr>
                <th style={{ textAlign: "left" }}>Ressort</th><th>Massnahmen</th><th>Anteil</th>
                <th>Umsetzungsgrad</th><th>KI-Massnahmen</th>
                <th style={{ textAlign: "left" }}>Teilprojekte (KI hervorgehoben)</th>
              </tr></thead>
              <tbody>
                {(blue.data.je_ressort ?? []).map((r) => (
                  <tr key={r.ressort}>
                    <td style={{ fontWeight: 600 }}>{r.ressort}</td>
                    <td style={{ textAlign: "center" }}>{r.anzahl}</td>
                    <td style={{ textAlign: "right" }}>{r.gewicht_pct} %</td>
                    <td style={{ textAlign: "right" }}>{r.umsetzungsgrad_pct == null ? "—" : r.umsetzungsgrad_pct + " %"}</td>
                    <td style={{ textAlign: "center" }}>
                      {r.ki_anzahl > 0 ? `${r.ki_anzahl} · ${r.ki_gewicht_pct} %` : "—"}
                    </td>
                    <td>
                      {(r.teilprojekte ?? []).map((tp) => (
                        <span key={tp} style={{
                          display: "inline-block", margin: "2px 4px 2px 0", padding: "1px 7px", borderRadius: 8,
                          fontSize: 11, border: "1px solid rgba(255,255,255,.18)",
                          background: (r.ki_teilprojekte ?? []).includes(tp) ? "rgba(80,160,255,.22)" : "transparent",
                          fontWeight: (r.ki_teilprojekte ?? []).includes(tp) ? 600 : 400,
                        }}>{tp}</span>
                      ))}
                      {(r.teilprojekte ?? []).length === 0 ? <span style={{ opacity: .5 }}>noch zu definieren</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sk-sub" style={{ marginTop: 10 }}>
            Anteil = Gewicht der Massnahmen eines Ressorts am Gesamtpotenzial des Programms.
            Umsetzungsgrad = gewichteter Fortschritt aus den gepflegten Status — kein gemessener Effekt.
          </p>
        </section>
      ) : null}

      {cellSel && (
        <CellPruefung
          sel={cellSel}
          api={api}
          canApply={isBerater}
          onClose={() => setCellSel(null)}
          onSaved={() => { setCellSel(null); qc.invalidateQueries({ queryKey: ["sk-board", effectiveTenantId] }); }}
        />
      )}

      <BeratertagePanel />

      <section className="sk-block sk-briefing">
        <div className="sk-block-head">
          <h2>KI-Steuerungsbriefing<Hint term="briefing" /></h2>
          <div className="sk-briefing-actions">
            <button className="btn primary" onClick={generateBriefing} disabled={running}>
              {running ? "KI schreibt das Briefing …" : "KI-Steuerungsbriefing erzeugen"}
            </button>
            {isBerater && latest?.markdown && (
              <>
                <button className="btn ghost small" onClick={() => exportBriefing("md")}>Export Markdown</button>
                <button className="btn ghost small" onClick={() => exportBriefing("xlsx")}>Export Excel</button>
              </>
            )}
          </div>
        </div>
        {running && (
          <div className="agent-progress"><div className="agent-progress-bar"><span /></div>
            <p className="muted small">Ein lokales Modell (DeepSeek) liest die echten Daten und schreibt daraus ein Briefing — nichts wird erfunden. Dauert ein bis zwei Minuten; das Fenster darf zu.</p></div>
        )}
        {err && <p className="error">{err}</p>}
        {latest?.markdown ? (
          <div className="sk-briefing-view">
            <p className="muted small">
              {latest.title} · Modell {latest.model_route} · {latest.measured_count}/{latest.kpi_count} Kennzahlen gemessen
              {latest.degraded ? " · ohne Modell erzeugt (nur Faktenteil)" : ""}
            </p>
            <Markdown text={latest.markdown} />
          </div>
        ) : !running && (
          <div className="empty-friendly"><strong>Noch kein Briefing.</strong>
            <p className="muted">Auf „KI-Steuerungsbriefing erzeugen“ klicken — ideal direkt vor dem Steuerkreis-Termin.</p></div>
        )}
      </section>

      <section className="sk-block sk-ask">
        <div className="sk-block-head">
          <h2>Frag die Daten<Hint term="frag_die_daten" /></h2>
          <span className="muted small">Antwort ausschließlich aus den echten Programmdaten · lokales Modell</span>
        </div>
        <div className="sk-ask-row">
          <input
            type="text" value={question} placeholder="z. B. Welche Ressorts sind in Verzug?"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
          />
          <button className="btn primary" onClick={() => ask()} disabled={asking || !question.trim()}>
            {asking ? "Frage läuft …" : "Fragen"}
          </button>
        </div>
        <div className="sk-suggests">
          {SUGGESTIONS.map((s) => (
            <button key={s} className="sk-chip" onClick={() => { setQuestion(s); ask(s); }} disabled={asking}>{s}</button>
          ))}
        </div>
        {askErr && <p className="error">{askErr}</p>}
        {answer && (
          <div className={`sk-answer ${answer.degraded ? "degraded" : ""}`}>
            <Markdown text={answer.answer} />
            <p className="muted small">
              {answer.degraded ? "Hinweis: Das Modell war nicht erreichbar — bitte den Rohdaten oben vertrauen." :
                `Quelle: ${answer.source ?? "Faktenblock"} · Modell ${answer.model ?? "lokal"}`}
            </p>
          </div>
        )}
      </section>

      <section className="sk-block">
        <div className="sk-block-head"><h2>In die Systeme springen</h2></div>
        <div className="sk-links">
          <Link className="sk-link" to="/app/start">
            <strong>Single Pane — alle Systeme</strong>
            <span>Zentrale Startsicht: Module, Dashboards &amp; Fachsysteme aus einem Guss</span>
          </Link>
          <a className="sk-link" href={`${OPENPROJECT_BASE}/projects`} target="_blank" rel="noopener">
            <strong>OpenProject<Hint term="openproject" /></strong>
            <span>Arbeitspakete &amp; Teilprojekte des 1&amp;1-Programms<Hint term="wp_zaehlung" /></span>
          </a>
          <a className="sk-link" href={OPENSEARCH_COCKPIT} target="_blank" rel="noopener noreferrer">
            <strong>OpenSearch-Cockpit</strong>
            <span>Programm- &amp; Gold-KPI-Dashboard</span>
          </a>
          <a className="sk-link" href={dashboardUrl("oe-board")} target="_blank" rel="noopener noreferrer">
            <strong>OE-Board</strong>
            <span>Ressort &times; Phase — Gate-Matrix (OpenSearch)</span>
          </a>
          <a className="sk-link" href={dashboardUrl("steuerung-wirkung")} target="_blank" rel="noopener noreferrer">
            <strong>Steuerung &amp; Wirkung</strong>
            <span>Methodik, Runs &amp; Wirkungs-Map (OpenSearch)</span>
          </a>
          <a className="sk-link" href={dashboardUrl("systemgesundheit")} target="_blank" rel="noopener noreferrer">
            <strong>Systemgesundheit</strong>
            <span>Betriebs-/Health-Dashboard (OpenSearch)</span>
          </a>
          <a className="sk-link" href="https://1und1os.impulse.kim" target="_blank" rel="noopener noreferrer">
            <strong>1und1os-Portal</strong>
            <span>KI-Betriebssystem: KIM-Chat, Apps &amp; Agent-Room</span>
          </a>
          <a className="sk-link" href="https://1und1-chat.ki-guru.com" target="_blank" rel="noopener noreferrer">
            <strong>1&amp;1-KIM-Chatbot</strong>
            <span>Fragen an Wissensbasis &amp; KPIs im Chat (gleiche Datenbasis)</span>
          </a>
          <a className="sk-link" href="https://cloud.impulse.kim" target="_blank" rel="noopener noreferrer">
            <strong>Nextcloud</strong>
            <span>Dateiablage 1und1KIM_OS: Protokolle, Decks, Contracting</span>
          </a>
        </div>
      </section>

      {isBerater && b?.available && b.ressorts.length > 0 && (
        <section className="sk-block">
          <div className="sk-block-head"><h2>Ressort-Detail (Beratersicht)</h2>
            <span className="muted small">Leads, Impulse-Team, Teilprojekte, SPoC</span></div>
          <div className="sk-ressorts">
            {b.ressorts.map((r) => (
              <article key={r.ressort} className="sk-ressort">
                <h3>{r.label}</h3>
                <dl>
                  <div><dt>Lead (1&amp;1)</dt><dd>{r.lead ?? "—"}</dd></div>
                  <div><dt>Impulse-Team</dt><dd>{(r.impulse_team ?? []).join(", ") || "—"}</dd></div>
                  <div><dt>SPoC</dt><dd>{r.spoc && r.spoc !== "N.N." ? r.spoc : "offen"}</dd></div>
                  <div><dt>HR-OE-Experte</dt><dd>{r.hr_oe_experte && r.hr_oe_experte !== "N.N." ? r.hr_oe_experte : "offen"}</dd></div>
                </dl>
                {(r.tps ?? []).length > 0 && (
                  <ul className="sk-tps">{(r.tps ?? []).map((t) => <li key={t}>{t}</li>)}</ul>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <footer className="sk-footer muted small">
        Nichts wird automatisch angewendet · jede Aussage hat einen Beleg · fehlende Messwerte werden als solche gekennzeichnet ·
        Datenstand {fmtDate(summary.data?.generated_at)}.
      </footer>
    </section>
  );
}


/** ---- Menschliche Querprüfung je Matrixzelle (dev-v141) ----
 *  Grundsatz: Die führende Quelle (OE-Board) bleibt unverändert. Bestätigung und
 *  Korrektur sind personengebundene Prüfvermerke; die Frage-KI ist strikt lesend
 *  und antwortet nur aus den Quellen dieser Zelle. */
const RV_AMPEL: Record<string, "gruen" | "gelb" | "rot"> = {
  in_line: "gruen", nicht_begonnen: "gelb", in_verzug: "rot",
};
const RV_OPTIONEN: Array<{ key: string; label: string }> = [
  { key: "in_line", label: "im Plan / in Arbeit (grün)" },
  { key: "nicht_begonnen", label: "noch nicht begonnen (gelb)" },
  { key: "in_verzug", label: "in Verzug (rot)" },
];
type EvidenceResp = {
  ressort: string; ressort_label?: string; phase: string;
  board_dokumente?: Array<Record<string, unknown>>;
  arbeitspakete?: Array<{ titel?: string; status?: string | null; projekt?: string | null; aktualisiert?: string | null }>;
  pruefvermerk?: CellReview | null; herkunft?: string;
};

function CellPruefung({ sel, api, canApply, onClose, onSaved }: {
  sel: CellSel; api: ReturnType<typeof useApi>; canApply: boolean; onClose: () => void; onSaved: () => void;
}) {
  const [ev, setEv] = useState<EvidenceResp | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [ans, setAns] = useState<AskResp | null>(null);
  const [asking, setAsking] = useState(false);
  const [korrOffen, setKorrOffen] = useState(false);
  const [corr, setCorr] = useState("nicht_begonnen");
  const [comment, setComment] = useState("");
  const [refs, setRefs] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setEv(null); setLoadErr(null); setAns(null); setKorrOffen(false); setComment(""); setSaveErr(null);
    api.get<EvidenceResp>(`/transformation/cell-evidence?ressort=${encodeURIComponent(sel.ressort)}&phase=${encodeURIComponent(sel.phase)}`)
      .then((r) => { if (live) setEv(r); })
      .catch((e) => { if (live) setLoadErr(String(e)); });
    return () => { live = false; };
  }, [sel.ressort, sel.phase]);

  const save = async (action: "bestaetigen" | "korrigieren" | "zuruecksetzen") => {
    setSaving(true); setSaveErr(null);
    try {
      await api.post(`/transformation/cell-review`, {
        ressort: sel.ressort, phase: sel.phase, action,
        corrected_status: action === "korrigieren" ? corr : undefined,
        comment,
        evidence_refs: action === "korrigieren"
          ? refs.split("\n").map((x) => x.trim()).filter(Boolean)
          : undefined,
      });
      onSaved();
    } catch (e) { setSaveErr(String(e)); } finally { setSaving(false); }
  };

  const askCell = async () => {
    const text = q.trim(); if (!text || asking) return;
    setAsking(true); setAns(null);
    try {
      const r = await api.post<AskResp>(`/transformation/cell-ask`, { ressort: sel.ressort, phase: sel.phase, question: text });
      setAns(r);
    } catch (e) { setAns({ answer: `Anfrage fehlgeschlagen: ${String(e)}` }); }
    finally { setAsking(false); }
  };

  const S: Record<string, any> = {
    wrap: { position: "fixed", inset: 0, background: "rgba(4,10,18,0.55)", zIndex: 90 },
    panel: { position: "fixed", top: 0, right: 0, height: "100vh", width: "min(460px, 92vw)",
      overflowY: "auto", background: "#0e1726", borderLeft: "1px solid #24354a",
      padding: "16px 18px 28px", boxShadow: "-12px 0 32px rgba(0,0,0,0.45)" },
    h: { margin: "0 0 2px", fontSize: 16 },
    sub: { margin: "0 0 12px", color: "#8fa3b8", fontSize: 12.5 },
    box: { background: "#111e30", border: "1px solid #24354a", borderRadius: 10, padding: "10px 12px", margin: "10px 0" },
    boxTitle: { margin: "0 0 6px", fontSize: 12, letterSpacing: 0.4, textTransform: "uppercase", color: "#7f95ac" },
    kv: { margin: "2px 0", fontSize: 13, color: "#d7e1ec" },
    small: { fontSize: 12, color: "#8fa3b8" },
    btnRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
    input: { width: "100%", boxSizing: "border-box", background: "#0b1220", color: "#e6eef7",
      border: "1px solid #2c405a", borderRadius: 8, padding: "8px 10px", fontSize: 13 },
    close: { position: "absolute", top: 10, right: 12, background: "none", border: "none",
      color: "#8fa3b8", fontSize: 20, cursor: "pointer" },
  };
  const rv = ev?.pruefvermerk ?? null;

  return (
    <div style={S.wrap} onClick={onClose}>
      <aside style={S.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Herkunft und Prüfung">
        <button style={S.close} onClick={onClose} title="Schließen">×</button>
        <h3 style={S.h}>{sel.label}</h3>
        <p style={S.sub}>Phase {sel.nr}: {sel.titel} · Anzeige: {AMPEL_TXT[sel.ampel] ?? sel.ampel}</p>

        <div style={S.box}>
          <p style={S.boxTitle}>Prüfstand</p>
          {rv ? (
            <>
              <p style={S.kv}>{rv.status === "bestaetigt" ? "✓ menschlich bestätigt" : "✎ menschlich korrigiert"} von <strong>{rv.reviewed_by ?? "unbekannt"}</strong></p>
              {rv.status === "korrigiert" && (
                <p style={S.kv}>Korrektur: <strong>{RV_OPTIONEN.find((o) => o.key === rv.corrected_status)?.label ?? rv.corrected_status}</strong> (Quelle sagt: {rv.original_status ?? "—"})</p>
              )}
              {rv.comment ? <p style={S.kv}>Begründung: {rv.comment}</p> : null}
              {(rv.evidence_refs ?? []).length > 0 && (
                <p style={S.kv}>Verweise: {(rv.evidence_refs ?? []).join(" · ")}</p>
              )}
              <p style={S.small}>{fmtDate(rv.reviewed_at)}</p>
              {rv.applied_at ? (
                <p style={{ ...S.kv, color: "#7ee2a8" }}>In das OE-Board übernommen am {fmtDate(rv.applied_at)} — die Quelle trägt jetzt die Herkunft „menschliche Prüfung".</p>
              ) : rv.status === "korrigiert" && canApply ? (
                <div style={S.btnRow}>
                  <button className="btn primary" disabled={saving}
                    onClick={async () => { setSaving(true); setSaveErr(null);
                      try { await api.post(`/transformation/cell-apply`, { ressort: sel.ressort, phase: sel.phase }); onSaved(); }
                      catch (e) { setSaveErr(String(e)); } finally { setSaving(false); } }}>
                    In OE-Board übernehmen (Quelle aktualisieren)
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p style={S.kv}>Noch von keinem Menschen geprüft.</p>
          )}
          <div style={S.btnRow}>
            <button className="btn primary" disabled={saving} onClick={() => save("bestaetigen")}>Status bestätigen</button>
            <button className="btn ghost" disabled={saving} onClick={() => setKorrOffen(!korrOffen)}>{korrOffen ? "Korrektur schließen" : "Korrigieren …"}</button>
            {rv && <button className="btn ghost small" disabled={saving} onClick={() => save("zuruecksetzen")}>Prüfvermerk zurücksetzen</button>}
          </div>
          {korrOffen && (
            <div style={{ marginTop: 10 }}>
              <select style={S.input} value={corr} onChange={(e) => setCorr(e.target.value)}>
                {RV_OPTIONEN.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <textarea style={{ ...S.input, marginTop: 8, minHeight: 64 }} placeholder="Begründung (Pflicht bei Korrektur) — z. B.: Gespräche laufen noch, Ressort ist tatsächlich in Auftragsklärung; Kickoff steht aus."
                value={comment} onChange={(e) => setComment(e.target.value)} />
              <textarea style={{ ...S.input, marginTop: 8, minHeight: 56 }} placeholder="Quellverweise — einer je Zeile (z. B. Meetily-Gespräch 24.07., OpenProject-WP #123, Whiteboard IMG_7844)"
                value={refs} onChange={(e) => setRefs(e.target.value)} />
              <div style={S.btnRow}>
                <button className="btn primary" disabled={saving || !comment.trim()} onClick={() => save("korrigieren")}>Korrektur speichern</button>
              </div>
            </div>
          )}
          {saveErr && <p style={{ ...S.small, color: "#ff9b9b" }}>{saveErr}</p>}
          <p style={S.small}>Die führende Quelle (OE-Board) bleibt unverändert — Bestätigung und Korrektur sind personengebundene Prüfvermerke.</p>
        </div>

        <div style={S.box}>
          <p style={S.boxTitle}>Wie kommt die Aussage zustande?</p>
          {loadErr ? <p style={{ ...S.kv, color: "#ff9b9b" }}>{loadErr}</p>
            : !ev ? <p style={S.small}>Quellen werden geladen …</p>
            : <p style={S.kv}>{ev.herkunft}</p>}
        </div>

        {ev && (
          <div style={S.box}>
            <p style={S.boxTitle}>Quelldokumente (OE-Board)</p>
            {(ev.board_dokumente ?? []).length === 0 && <p style={S.small}>Keine Dokumente gefunden.</p>}
            {(ev.board_dokumente ?? []).map((d, i) => (
              <p key={i} style={S.kv}>
                {String((d as any).doc_type ?? "dokument")}: {String((d as any).phase_titel ?? (d as any).ressort_label ?? "")}
                {(d as any).status ? ` · Status „${String((d as any).status)}"` : ""}
                {(d as any).lead ? ` · Lead ${String((d as any).lead)}` : ""}
                {(d as any).kommentar ? ` · Beleg: ${String((d as any).kommentar)}` : ""}
                {(d as any).source ? ` · Quelle ${String((d as any).source)}` : ""}
                {(d as any).updated_at ? ` · ${fmtDate(String((d as any).updated_at))}` : ""}
              </p>
            ))}
          </div>
        )}

        {ev && (ev.arbeitspakete ?? []).length > 0 && (
          <div style={S.box}>
            <p style={S.boxTitle}>Verwandte Arbeitspakete (OpenProject, nur lesend)</p>
            {(ev.arbeitspakete ?? []).map((w, i) => (
              <p key={i} style={S.kv}>{w.titel} {w.status ? `· ${w.status}` : ""} {w.projekt ? `· ${w.projekt}` : ""}</p>
            ))}
          </div>
        )}

        <div style={S.box}>
          <p style={S.boxTitle}>Frage an die Quellen (lesende KI)</p>
          <input style={S.input} value={q} placeholder="z. B. Woher kommt dieser Status, und was würde ihn ändern?"
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") askCell(); }} />
          <div style={S.btnRow}>
            <button className="btn primary" disabled={asking || !q.trim()} onClick={askCell}>{asking ? "Liest die Quellen …" : "Fragen"}</button>
          </div>
          {ans && (
            <div style={{ marginTop: 10 }}>
              <Markdown text={ans.answer} />
              <p style={S.small}>{ans.degraded ? "Modell nicht erreichbar — Rohquellen oben bleiben gültig." : `Antwort strikt aus den Zell-Quellen · Modell ${ans.model ?? "lokal"}`}</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
